import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { GoogleMap, Marker, Polygon, InfoWindow, useLoadScript } from "@react-google-maps/api";
import mapStyle from "../../data/map.json";
import provinces from "../../data/map_data.json"; // ✅ Updated JSON source

const ViewGMap = () => {
    const mapRef = useRef(null);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY,
        libraries: ["drawing"],
    });

    const stations = useMemo(() => {
        const results = [];
        provinces.forEach(prov => {
            prov.pollings_station?.forEach(ps => {
                if (
                    Array.isArray(ps.coordinates) &&
                    ps.coordinates.length === 2 &&
                    !(ps.coordinates[0] === 0 && ps.coordinates[1] === 0)
                ) {
                    results.push({
                        id: ps.id,
                        name: ps.name,
                        delimitation_code: ps.delimitation_code,
                        lat: ps.coordinates[1],
                        lng: ps.coordinates[0],
                        provinceName: prov.name
                    });
                }
            });
        });
        return results;
    }, []);

    const provinceColors = useMemo(() => {
        const colors = [
            "red", "blue", "green", "purple", "orange",
            "cyan", "magenta", "yellow", "teal", "gray"
        ];
        const mapping = {};
        provinces.forEach((prov, i) => {
            mapping[prov.name] = colors[i % colors.length];
        });
        return mapping;
    }, []);

    const onLoad = useCallback((map) => {
        mapRef.current = map;
        if (stations.length > 0 && window.google) {
            const bounds = new window.google.maps.LatLngBounds();
            stations.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
            map.fitBounds(bounds);
        }
    }, [stations]);

    useEffect(() => {
        if (mapRef.current) {
            window.dispatchEvent(new Event("resize"));
        }
    }, []);

    if (loadError) return <div>Error loading map</div>;
    if (!isLoaded) return <div>Loading Map...</div>;

    return (
        <div>
            <GoogleMap
                mapContainerStyle={{ width: "100%", height: "600px" }}
                onLoad={onLoad}
                options={{ styles: mapStyle }}
            >
                {/* Province polygons */}
                {provinces.map((prov, index) => {
                    if (
                        prov.geometry?.coordinates?.length > 0 &&
                        Array.isArray(prov.geometry.coordinates[0])
                    ) {
                        const path = prov.geometry.coordinates[0].map(([lng, lat]) => ({
                            lat,
                            lng,
                        }));

                        return (
                            <Polygon
                                key={`province-${index}`}
                                path={path}
                                options={{
                                    strokeColor: provinceColors[prov.name],
                                    strokeOpacity: 0.8,
                                    strokeWeight: 2,
                                    fillColor: provinceColors[prov.name],
                                    fillOpacity: 0.15,
                                }}
                            />
                        );
                    }
                    return null;
                })}

                {/* Polling station markers */}
                {stations.map((station) => (
                    <Marker
                        key={station.id}
                        position={{ lat: station.lat, lng: station.lng }}
                        title={station.name}
                        icon={{
                            url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                        }}
                        onClick={() => setSelectedMarker(station)}
                    />
                ))}

                {/* Info window on marker click */}
                {selectedMarker && (
                    <InfoWindow
                        position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <div>
                            <strong>{selectedMarker.name}</strong><br />
                            Code: {selectedMarker.delimitation_code}
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
};

export default ViewGMap;
